import 'firebase/auth'
import 'firebase/firestore'
import 'firebase/storage'
import 'firebase/functions'
import urlHelper from '../helpers/url'
import * as YoutubeAPI from '../api/YoutubeAPI'
import * as VimeoAPI from '../api/VimeoAPI'

class Firebase {
  constructor(app) {
    this.auth = app.auth()
    this.db = app.firestore()
    this.storage = app.storage()
    this.functions = app.functions()

    this.cachedVideoSnippets = {}
  }

  doCreateUserWithEmailAndPassword = (email, password) =>
    this.auth.createUserWithEmailAndPassword(email, password)

  signOut = () => this.auth.signOut()

  doSignInWithEmailAndPassword = (email, password) =>
    this.auth.signInWithEmailAndPassword(email, password)

  userId = () => this.auth.currentUser.uid.toString()

  userEmail = () => this.auth.currentUser.email.toString()

  doPasswordReset = (email) => this.auth.sendPasswordResetEmail(email)

  doPasswordUpdate = (password) =>
    this.auth.currentUser.updatePassword(password)

  usersCollection = () => this.db.collection('users')

  userSettings = () => this.currentUser().collection('settings')

  user = (userId) => this.usersCollection().doc(userId)

  currentUser = () => this.usersCollection().doc(this.userId())

  contentCollection = () => this.currentUser().collection('content')

  contentWithId = (contentId) => this.contentCollection().doc(contentId)

  permission(contentId, recipientEmail) {
    return this.contentWithId(contentId)
      .collection('permissions')
      .doc(recipientEmail)
  }

  permissions = () => this.db.collectionGroup('permissions')

  permissionsGiven() {
    return this.db
      .collectionGroup('permissions')
      .where('owner', '==', this.userId())
  }

  addPermissions = (contentId, emails) => {
    const ownerId = this.userId()

    const count = emails.length
    const chunkSize = 500

    for (let i = 0, j = count; i < j; i += chunkSize) {
      const chunk = emails.slice(i, i + chunkSize)

      const batch = this.db.batch()
      chunk.forEach((email) =>
        batch.set(this.permission(contentId, email), {
          owner: ownerId,
          recipient: email,
          permission: 'share',
        }),
      )
      batch.commit()
    }
  }

  removePermission = (contentId, recipientEmail) =>
    this.permission(contentId, recipientEmail).delete()

  saveContent = (contentId, content) =>
    this.contentWithId(contentId).set({
      content: JSON.stringify(content),
      id: contentId,
    })

  deleteContent = (contentId) => this.contentWithId(contentId).delete()

  setBoardsOrder = (order) =>
    this.currentUser().collection('settings').doc('content').set({
      order,
    })

  putImage = (image, name) => this.storage.ref(`/images/${name}`).put(image)

  getVideoSnippet = async (url) => {
    const videoDetails = urlHelper.getVideoDetails(url)
    if (videoDetails === null) {
      return null
    }

    const key = `${videoDetails.type}-${url}`
    if (this.cachedVideoSnippets[key] === undefined) {
      switch (videoDetails.type) {
        case 'youtube':
          const snippet = await this.functions.httpsCallable(
            'getYouTubeVideoSnippet',
          )({ client: 'browser', videoId: videoDetails.id })
          this.cachedVideoSnippets[key] = YoutubeAPI.getInfo(snippet)
          break
        case 'vimeo':
          this.cachedVideoSnippets[key] = VimeoAPI.getInfo(videoDetails.id)
          break
        default:
          this.cachedVideoSnippets[key] = null
      }
    }
    return this.cachedVideoSnippets[key]
  }
}

export default Firebase
